import React, { useEffect, useState, Suspense } from "react"
import { useLocation } from "react-router-dom"
import { LazyLoadImage } from "react-lazy-load-image-component"
import "react-lazy-load-image-component/src/effects/blur.css"
import articleApi from "../api/articleApi"
import portalsApi from "../api/portalsApi"
import categoriesApi from "../api/categoriesApi"
import { stripHtmlTags } from "../utils/stripHtmlTags"
import renderPaginationButtons from "../components/pagination"
import LikeButton from "../components/likeButton"
import Spinner from "../components/spinner"
import { useNavigate } from "react-router-dom"
import { getTimeAgo } from "../utils/getTimeAgo"
import ImageWithFallback from "../helpers/ImageWithFallback"
import PortalLogoFallback from "../helpers/PortalLogoFallback"

// Loading skeleton component
const SearchResultSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(18)].map((_, i) => (
      <div key={i} className="bg-white dark:bg-gray-800 overflow-hidden animate-pulse">
        <div className="w-full h-48 bg-gray-200 dark:bg-gray-700"></div>
        <div className="p-4">
          <div className="flex items-start mb-2">
            <div className="w-1 h-4 bg-gray-200 dark:bg-gray-700 mr-2 mt-1"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            </div>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
          <div className="space-y-2 mb-4">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="flex items-center justify-between">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
)

const SearchResultsPage = () => {
  const location = useLocation()
  const [searchResults, setSearchResults] = useState([])
  const [categories, setCategories] = useState([])
  const [portals, setPortals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize] = useState(18)
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)
  const query = searchParams.get("q")

  // Fetch categories and portals
  useEffect(() => {
    const fetchCategoriesAndPortals = async () => {
      try {
        const [categoriesRes, portalsRes] = await Promise.all([
          categoriesApi.getAll(),
          portalsApi.getAll(),
        ])

        setCategories(categoriesRes.data)
        setPortals(portalsRes.data)
      } catch (err) {
        console.error("Error fetching categories or portals:", err)
        setError(err.message)
      }
    }
    fetchCategoriesAndPortals()
  }, [])

  // Fetch search results
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (query && query.length > 2) {
        try {
          setLoading(true)
          const response = await articleApi.getAll({
            search: query,
            page,
            page_size: pageSize,
          })

          const sortedResults = response.data.results.sort(
            (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
          )

          setSearchResults(sortedResults)
          setTotalPages(Math.ceil(response.data.count / pageSize))
          setLoading(false)
        } catch (err) {
          setError("Failed to fetch search results.")
          setLoading(false)
        }
      }
    }

    fetchSearchResults()
  }, [query, page, pageSize])

  // Handle pagination change
  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  // Find category name
  const getCategoryName = categoryId => {
    const category = categories.find(cat => cat.id === categoryId)
    return category ? category.name : ""
  }

  const handlePortalClick = (portal, event) => {
    event.stopPropagation()
    if (portal) {
      const sanitizedPortalName = stripHtmlTags(portal.name)
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9-]/g, "")
        .toLowerCase()

      localStorage.setItem("portalName", sanitizedPortalName)
      localStorage.setItem("portalId", portal.id)
      navigate(`/portal/${sanitizedPortalName}/`)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const handleCategoryClick = (id, name, event) => {
    event.stopPropagation()
    const sanitizedCategoryName = name.toLowerCase().replace(/\s+/g, "-")
    localStorage.setItem("categoryName", sanitizedCategoryName)
    localStorage.setItem("categoryId", id)
    navigate(`/category/${sanitizedCategoryName}`)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleCardClick = (e, articleId) => {
    // Prevent navigation if an interactive element (like button or img) is clicked
    if (
      e.target.tagName === "BUTTON" ||
      e.target.closest("button") ||
      e.target.closest("img")
    ) {
      return
    }
    navigate(`/article/${articleId}`)
  }

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6 animate-pulse"></div>
        <SearchResultSkeleton />
      </div>
    )
  }

  if (error) return <p className="text-red-500">{error}</p>

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Search Results for "{query}"</h1>

      {searchResults.length > 0 ? (
        <Suspense fallback={<SearchResultSkeleton />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {searchResults.map(article => {
              // Handle both old structure (portal as ID) and new structure (portal as object)
              const portal = typeof article.portal === 'object' ? article.portal : portals.find(p => p.id === article.portal)
              const portalLogo = portal?.logo_url || ""
              const portalName = portal?.name || ""
              
              const categoryName = getCategoryName(
                article?.categories_related?.[0]
              )

              // Handle thumbnail URL
              const thumbnailUrl = article.thumbnail?.startsWith("http")
                ? article.thumbnail
                : article.thumbnail
                ? `${process.env.REACT_APP_API_URL}${article.thumbnail}`
                : "https://via.placeholder.com/300"

              return (
                <div
                  key={article.id}
                  onClick={e => handleCardClick(e, article.id)}
                  className="relative bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transform ease-in-out cursor-pointer group hover:shadow-md transition-all duration-200"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <LazyLoadImage
                      src={thumbnailUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      effect="blur"
                      placeholder={
                        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                      }
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start">
                      <div style={{ 
                        width: '4px', 
                        minWidth: '4px', 
                        height: '14px', 
                        backgroundColor: '#DC2626', 
                        marginRight: '8px', 
                        marginTop: '4px', 
                        flexShrink: 0 
                      }}></div>
                      <h5 className="text-lg font-bold text-gray-900 line-clamp-2 mt-0 dark:text-gray-100 mb-2 group-hover:underline transition-all">
                        {article.title}
                      </h5>
                    </div>
                    
                    {categoryName && (
                      <p
                        className="text-sm text-gray-500 dark:text-gray-300 mb-1 cursor-pointer hover:underline"
                        onClick={(event) =>
                          handleCategoryClick(
                            article?.categories_related?.[0],
                            categoryName,
                            event
                          )
                        }
                      >
                        {categoryName}
                      </p>
                    )}
                    
                    <p className="text-sm text-gray-700 dark:text-gray-400 mb-4 line-clamp-2">
                      {stripHtmlTags(article.content).substring(0, 135)}...
                    </p>
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {getTimeAgo(article.created_at)}
                    </p>
                    
                    <div className="flex items-center justify-between mt-4">
                      <LikeButton
                        articleId={article.id}
                        initialLikeStatus={article.liked}
                        likesCount={article.likes_count}
                      />
                      
                      {portal && (
                        <ImageWithFallback 
                          src={portalLogo} 
                          className="w-10 h-10 object-contain ml-4 rounded-full border border-gray-300 cursor-pointer hover:border-gray-400 transition-colors" 
                          fallback={
                            <PortalLogoFallback 
                              portalName={portalName} 
                              isOverlay={false} 
                            />
                          } 
                          onClick={event => handlePortalClick(portal, event)} 
                        />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </Suspense>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            No results found for "{query}".
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
            Try adjusting your search terms or check the spelling.
          </p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-8 space-x-2">
          {renderPaginationButtons({
            totalPages,
            currentPage: page,
            handlePageChange,
          })}
        </div>
      )}
    </div>
  )
}

export default SearchResultsPage
